import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface MFAChallenge {
  factorId: string;
  email?: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any; mfaRequired?: boolean }>;
  signOut: () => Promise<void>;
  isAdmin: boolean;
  mfaChallenge: MFAChallenge | null;
  clearMFAChallenge: () => void;
  completeMFASignIn: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [mfaChallenge, setMfaChallenge] = useState<MFAChallenge | null>(null);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        
        // Check user role after auth state changes
        if (session?.user) {
          setTimeout(() => {
            checkUserRole(session.user.id);
          }, 0);
        } else {
          setIsAdmin(false);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      
      if (session?.user) {
        setTimeout(() => {
          checkUserRole(session.user.id);
        }, 0);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // SECURITY NOTE: This client-side admin check is for UI/UX only.
  // The actual security enforcement happens via Row-Level Security (RLS) policies
  // in the database. Even if an attacker manipulates this client-side state to
  // display admin UI elements, they cannot access protected data because all
  // database operations are protected by RLS policies that verify roles server-side.
  const checkUserRole = async (userId: string) => {
    try {
      // Call server-side RPC to check if user has admin role
      const { data, error } = await supabase.rpc('has_role', {
        _user_id: userId,
        _role: 'admin'
      });

      if (error) {
        console.log('Error checking user role via RPC:', error);
        setIsAdmin(false);
        return;
      }

      // Store admin status in React state for UI rendering decisions
      setIsAdmin(Boolean(data));
    } catch (error) {
      console.log('Unexpected error checking user role:', error);
      setIsAdmin(false);
    }
  };

  const signUp = async (email: string, password: string, fullName?: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
        }
      }
    });

    if (error) {
      toast.error(error.message);
    } else {
      toast.success('Check your email for the confirmation link!');
    }

    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      toast.error(error.message);
      return { error };
    }

    // Check if MFA is required
    const { data: factorsData } = await supabase.auth.mfa.listFactors();
    const verifiedFactors = factorsData?.totp?.filter(f => f.status === 'verified') || [];

    if (verifiedFactors.length > 0) {
      // User has MFA enabled, need to verify
      setMfaChallenge({
        factorId: verifiedFactors[0].id,
        email
      });
      return { error: null, mfaRequired: true };
    }

    toast.success('Successfully signed in!');
    return { error: null };
  };

  const clearMFAChallenge = () => {
    setMfaChallenge(null);
  };

  const completeMFASignIn = () => {
    setMfaChallenge(null);
    toast.success('Successfully signed in!');
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast.error(error.message);
    } else {
      setMfaChallenge(null);
      toast.success('Successfully signed out!');
    }
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signOut,
    isAdmin,
    mfaChallenge,
    clearMFAChallenge,
    completeMFASignIn,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
